import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@shared/ui';
import type { ManufacturerProfile, UpdateProfileRequest } from '@entities/manufacturer';
import { useUpdateProfile } from '../model/mutations';
import { ProfileFields } from './profile-fields';
import { DocumentUploader } from './document-uploader';

const schema = z.object({
  companyName: z.string().min(1).max(256),
  description: z.string().optional().or(z.literal('')),
  country: z.string().max(2).optional().or(z.literal('')),
  website: z.string().optional().or(z.literal('')),
  foundedYear: z
    .union([z.coerce.number().int().min(1800).max(new Date().getUTCFullYear() + 1), z.nan()])
    .optional(),
  employeesRange: z.string().optional().or(z.literal('')),
  ecoProducer: z.boolean().default(false),
});
type Values = z.infer<typeof schema>;

export function EditProfileDialog({
  open,
  onOpenChange,
  profile,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  profile: ManufacturerProfile;
}) {
  const [categories, setCategories] = useState<string[]>(profile.categoryIds);
  const [logoMediaId, setLogoMediaId] = useState<string | null>(profile.logo?.id ?? null);
  const [videoMediaId, setVideoMediaId] = useState<string | null>(profile.video?.id ?? null);
  const [certificateMediaIds, setCertificateMediaIds] = useState<string[]>(
    profile.certificates.map((c) => c.id),
  );
  const [ecoCertificateMediaIds, setEcoCertificateMediaIds] = useState<string[]>(
    profile.ecoCertificates.map((c) => c.id),
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: profile.companyName,
      description: profile.description ?? '',
      country: profile.country ?? '',
      website: profile.website ?? '',
      foundedYear: profile.foundedYear ?? undefined,
      employeesRange: profile.employeesRange ?? '',
      ecoProducer: profile.ecoProducer,
    },
  });

  const save = useUpdateProfile(profile.id);

  const onSubmit = handleSubmit((v) => {
    const body: UpdateProfileRequest = {
      companyName: v.companyName,
      description: v.description || null,
      country: v.country || null,
      categories,
      logoMediaId,
      videoMediaId,
      certificateMediaIds,
      ecoCertificateMediaIds,
      ecoProducer: v.ecoProducer,
      website: v.website || null,
      foundedYear: Number.isFinite(v.foundedYear as number) ? (v.foundedYear as number) : null,
      employeesRange: v.employeesRange || null,
    };
    save.mutate(body, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Редактировать профиль</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
          <DialogBody className="space-y-4">
            <ProfileFields
              register={register}
              errors={errors}
              selectedCategoryIds={categories}
              onCategoriesChange={setCategories}
            />

            <div className="space-y-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Медиа
              </div>
              <DocumentUploader
                scope="PROFILE_LOGO"
                label="Логотип"
                multiple={false}
                mediaIds={logoMediaId ? [logoMediaId] : []}
                onChange={(ids) => setLogoMediaId(ids[0] ?? null)}
                accept="image/*"
                existing={profile.logo ? [profile.logo] : []}
              />
              <DocumentUploader
                scope="PRODUCT_VIDEO"
                label="Видео"
                multiple={false}
                mediaIds={videoMediaId ? [videoMediaId] : []}
                onChange={(ids) => setVideoMediaId(ids[0] ?? null)}
                accept="video/*"
                existing={profile.video ? [profile.video] : []}
              />
              <DocumentUploader
                scope="CERTIFICATE"
                label="Сертификаты / KYC"
                mediaIds={certificateMediaIds}
                onChange={setCertificateMediaIds}
                existing={profile.certificates}
              />
              <DocumentUploader
                scope="ECO_CERTIFICATE"
                label="Эко-сертификаты"
                mediaIds={ecoCertificateMediaIds}
                onChange={setEcoCertificateMediaIds}
                existing={profile.ecoCertificates}
              />
            </div>

            {profile.kycStatus === 'REJECTED' ? (
              <p className="rounded border border-warning/40 bg-warning/10 p-2 text-xs">
                KYC был отклонён. После сохранения статус сбросится в NONE — потребуется повторное
                подтверждение.
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Spinner /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
